import type { NavigationProps, NavItemButton, NavItemLink, NavSection } from "@wayflyer/flyui";
import { IconCheckSquares16Line } from "@wayflyer/flyui-icons/16/line";
//  These icons should actually be 20. When we roll out the missing ones above,
//   we should change all of them from 16 to 20 and remove the forced sizing in fefo
import {
  IconCity16Line,
  IconGift16Line,
  IconUserPlus16Line,
  IconSettings16Line,
  IconLogOut16Line,
  IconLotOfCash16Line,
  IconReports16Line,
  IconCreditCard16Line,
  IconGraphUp16Line,
  IconDollarCircle16Line,
  IconConnections16Line,
  IconOpenBook16Line,
  IconBank16Line,
  IconEmptyPage16Line,
  IconGroup16Line,
  IconHelpCircle16Line,
  IconWrench16Line,
} from "@wayflyer/flyui-icons-next/16/line";
import { BaseSchemas } from "api-types";
import { useFeatureFlagEnabled } from "posthog-js/react";
import type { ReactNode } from "react";
import { useIntl } from "react-intl";
import type { NavigateOptions } from "react-router-dom";
import { useInviteModalActions } from "shared-ui";

import { MAX_VISIBLE_ACCOUNTS } from "../../../banking/constants";

import { getNavItemNameFromId } from "./nav-item-strings";
import { useNavTaskNotificationCount } from "./nav-task-notification-count";

declare global {
  interface Window {
    Optanon: {
      ToggleInfoDisplay: () => void;
    };
  }
}
function getIconComponent(icon: string): ReactNode | undefined {
  if (typeof icon === "string") {
    switch (icon) {
      case "bank":
        return <IconLotOfCash16Line />;
      case "line-chart":
        return <IconReports16Line />;
      case "team":
        return <IconUserPlus16Line />;
      case "help":
        return <IconHelpCircle16Line />;
      case "connections":
        return <IconConnections16Line />;
      case "presentation-chart-line":
        return <IconReports16Line />;
      case "benchmarks":
        return <IconGraphUp16Line />;
      case "banking":
        return <IconCity16Line />;
      case "financial-os":
        return <IconGraphUp16Line />; // TODO: update
      case "internal":
        return <IconWrench16Line />;
      case "dollar":
        return <IconDollarCircle16Line />;
      case "share":
        return <IconGift16Line />;
      case "account":
        return <IconBank16Line />;
      case "payments":
        return <IconGraphUp16Line />;
      case "recipients":
        return <IconGroup16Line />;
      case "cards":
        return <IconCreditCard16Line />;
      case "transactions":
        return <IconOpenBook16Line />;
      case "documents":
        return <IconEmptyPage16Line />;
      case "tasks":
        return <IconCheckSquares16Line />;
      default:
        return;
    }
  }
}

const navItemMatches = (path: string, alternativePaths: string[] = [], pathname: string): boolean => {
  if (path === pathname || alternativePaths.includes(pathname)) {
    return true;
  } else {
    return alternativePaths.some((alternativePath) => pathname.startsWith(alternativePath));
  }
};

export const useNavigationItems = ({
  appConfig,
  clientNavigateFn,
}: {
  appConfig: BaseSchemas.CustomerAppConfig;
  clientNavigateFn: (path: string, options?: NavigateOptions) => void;
}): NavigationProps => {
  const intl = useIntl();
  const { navItems, customerDomain } = appConfig;
  const taskNotifications = useNavTaskNotificationCount(customerDomain);
  const enableClientSideLinks = useFeatureFlagEnabled("enable-all-client-side-customer-app-links");
  const { openInviteModal } = useInviteModalActions();
  const alternativePathsMap = {};

  const linkBehavior = enableClientSideLinks
    ? {}
    : {
        navigateFn: clientNavigateFn,
      };

  for (const navItem of navItems ?? []) {
    if (navItem.alternativePaths) {
      alternativePathsMap[navItem.path] = navItem.alternativePaths;
    }

    if (navItem.subItems) {
      for (const subItem of navItem.subItems) {
        if (subItem.alternativePaths) {
          alternativePathsMap[subItem.path] = subItem.alternativePaths;
        }
      }
    }
  }

  const getIsRouteSelected = (pathname: string): boolean => {
    return navItemMatches(pathname, alternativePathsMap[pathname], location.pathname);
  };

  function getClientSubNavItems(
    navItems: BaseSchemas.CustomerAppConfig["navItems"] | null | undefined
  ): Array<NavItemButton | NavItemLink> | undefined {
    if (!navItems || navItems.length === 0) {
      return;
    }

    return navItems.map(({ id, icon, path }) => {
      return {
        id,
        href: path,
        icon: getIconComponent(icon),
        label: getNavItemNameFromId(intl, id),
        ...linkBehavior,
      };
    });
  }

  const profileSection: NavigationProps["profileSection"] = {
    label: appConfig.customerName,
    id: "profile-section",
    icon: null,
    items: [
      {
        id: "settings",
        label: intl.formatMessage({ id: "Menu: Settings", defaultMessage: "Settings" }),
        href: `/${appConfig?.customerDomain}/settings`,
        icon: <IconCity16Line />,
        ...linkBehavior,
      },
      {
        id: "invite",
        label: intl.formatMessage({ id: "Invite someone", defaultMessage: "Invite a team member" }),
        onClick: () => openInviteModal(),
        icon: <IconGroup16Line />,
      },
      ...(!navItems || navItems.length === 0
        ? [
            {
              id: "help",
              label: intl.formatMessage({ id: "help centre title", defaultMessage: "Help center" }),
              href: "https://www.wayflyer.com/help-center",
              external: true,
              icon: <IconHelpCircle16Line />,
            },
          ]
        : []),
      ...(window?.Optanon?.ToggleInfoDisplay
        ? [
            {
              id: "privacy-settings",
              label: intl.formatMessage({
                id: "Privacy settings",
                defaultMessage: "Privacy settings",
              }),
              onClick: () => window.Optanon.ToggleInfoDisplay(),
              icon: <IconSettings16Line />,
            },
          ]
        : []),
      {
        id: "sign-out",
        label: intl.formatMessage({ id: "Sign out", defaultMessage: "Sign out" }),
        error: true,
        href: "/authn/logout",
        reloadDocument: enableClientSideLinks ? true : undefined,
        icon: <IconLogOut16Line />,
      },
    ],
  };

  const NAVIGABLE_SECTIONS = ["banking/payments", "banking/accounts"];

  const toClientNavItem = (navItem: BaseSchemas.NavItem): NavItemLink | NavSection => {
    const { id } = navItem;
    let items = getClientSubNavItems(navItem.subItems);
    const icon = getIconComponent(navItem.icon);
    const label = getNavItemNameFromId(intl, navItem.id);
    const navigableSection = navItem.path && NAVIGABLE_SECTIONS.some((section) => navItem.path.includes(section));

    const isAccountsSection = id.includes("accounts") || navItem.path?.includes("/accounts");

    if (isAccountsSection && Array.isArray(items) && items.length > 0) {
      if (items.length > MAX_VISIBLE_ACCOUNTS) {
        const visibleItems = items.slice(0, MAX_VISIBLE_ACCOUNTS);
        const remainingCount = items.length - MAX_VISIBLE_ACCOUNTS;

        const seeMoreItem: NavItemLink = {
          id: "banking-accounts-see-more",
          label: `See more (${remainingCount})`,
          href: "#",
          icon: undefined,
          navigateFn: () => {
            const accountsPath = navItem.path || "";
            clientNavigateFn(`${accountsPath}?expand=true`);
          },
        };

        items = [...visibleItems, seeMoreItem];
      }

      if (items.length === 1) {
        const singleAccountItem = items[0];
        const accountHref = "href" in singleAccountItem ? singleAccountItem.href : navItem.path;
        return {
          id,
          icon,
          label,
          href: accountHref,
          ...linkBehavior,
        };
      }
    }

    if (items) {
      return {
        id,
        icon,
        label,
        items,
        ...(navigableSection && {
          href: navItem.path,
          ...linkBehavior,
        }),
      };
    } else {
      return {
        id,
        icon,
        label,
        href: navItem.path,
        ...linkBehavior,
      };
    }
  };

  const sections: NavigationProps["sections"] = {
    navItems: (navItems ?? []).map(toClientNavItem).filter((item) => !item.href?.includes("customer-referrals")), //@todo: do this on the server,
  };

  const infoItems = [
    ...(navItems ?? []).map(toClientNavItem).filter((item) => item.href?.includes("customer-referrals")), //@todo: do this on the server,
    ...(navItems && navItems.length !== 0
      ? [
          {
            id: "help",
            label: intl.formatMessage({ id: "help centre title", defaultMessage: "Help center" }),
            href: "https://www.wayflyer.com/help-center",
            icon: <IconHelpCircle16Line />,
            external: true,
          },
        ]
      : []),
  ] as Array<NavItemLink>;

  const notificationBubble =
    taskNotifications > 0
      ? {
          navItemId: "customer tasks page title",
          count: taskNotifications,
          title: "customer tasks page title",
        }
      : undefined;

  return {
    profileSection,
    sections,
    infoItems,
    getIsRouteSelected,
    notificationBubble,
  };
};
